import { useState } from 'react';
import { AirportContextMenuDto } from '../../../airports/airport-dto';
import commonFormatter from '../../../formatters/common-formatter';
import { GetThresholdsForFlightResponse } from '../../../services/actor-messages';
import { AircraftThresholdType } from '../../../types/thresholds';
import { useAppSelector } from '../../hooks/store-hooks';
import SegmentedControl from '../inputs/SegmentedControl';
import AirportThresholds from './AirportThresholdsList';

export function AirportThresholdsNested(props: {
    airports: AirportContextMenuDto[];
    thresholdType: AircraftThresholdType;
    forceOnlyBreached?: boolean;
}) {
    const onlyShowBreached =
        useAppSelector((x) => x.settings.hideNonBreachedThresholdsAirportContextMenu) || (props.forceOnlyBreached ?? false);
    const [selectedAirport, setSelectedAirport] = useState(props.airports[0]?.id);
    const airportDisplayStyle = useAppSelector((x) => x.settings.airportIdDisplayStyle);

    const columnFilter =
        props.thresholdType === AircraftThresholdType.aptMonStatOrg
            ? 'origin'
            : props.thresholdType === AircraftThresholdType.aptMonStatDst
              ? 'destination'
              : props.thresholdType === AircraftThresholdType.aptMonStatAlt
                ? 'alternate'
                : null;
    return (
        <div>
            <div className="p-2">
                {thresholdTypeToName[props.thresholdType]}
                {props.airports.length === 1 ? (
                    <>
                        <span>: </span>
                        <strong>{commonFormatter.formatAirportId(props.airports[0], airportDisplayStyle)}</strong>
                    </>
                ) : (
                    <span>
                        <span>: </span>
                        <SegmentedControl
                            style={{ fontSize: '.9em' }}
                            className="d-inline-block"
                            data={props.airports.map((x) => ({
                                value: x.id,
                                label: commonFormatter.formatAirportId(x, airportDisplayStyle) ?? '?'
                            }))}
                            value={selectedAirport}
                            onChange={(e) => setSelectedAirport(e.value)}
                        />
                    </span>
                )}
            </div>

            {selectedAirport && (
                <AirportThresholds id={selectedAirport} onlyShowBreached={onlyShowBreached} columnsFilter={columnFilter!} />
            )}
        </div>
    );
}

export const thresholdTypeToName: Partial<Record<AircraftThresholdType, string>> = {
    [AircraftThresholdType.aptMonStatOrg]: 'Origin',
    [AircraftThresholdType.aptMonStatDst]: 'Destination',
    [AircraftThresholdType.aptMonStatAlt]: 'Alternate'
};

export function aircraftThresholdTypeToAirportIds(
    type: AircraftThresholdType,
    response: GetThresholdsForFlightResponse
): AirportContextMenuDto[] {
    if (type === AircraftThresholdType.aptMonStatOrg) {
        return response.airports.origin ? [response.airports.origin] : [];
    }

    if (type === AircraftThresholdType.aptMonStatDst) {
        return response.airports.destination ? [response.airports.destination] : [];
    }

    if (type === AircraftThresholdType.aptMonStatAlt) {
        return response.airports.alternates;
    }

    return [];
}

export function isThresholdAirportAvailable(type: AircraftThresholdType, response: GetThresholdsForFlightResponse): boolean {
    if (type === AircraftThresholdType.aptMonStatOrg) {
        return !!response.airports.origin;
    }

    if (type === AircraftThresholdType.aptMonStatDst) {
        return !!response.airports.destination;
    }

    if (type === AircraftThresholdType.aptMonStatAlt) {
        return (response.airports.alternates?.length ?? 0) > 0;
    }

    return false;
}
